import type * as I from "core/api/coreApiClient";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";

export const pluralize = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural);

const I18n = {
  utils: {
    commandsWithSelectionState: {
      saving: "Saving row selection...",
      requiresNumber: (count: number, title: string) =>
        `Requires ${pluralize(count, "1 row", `${count} rows`)} minimum to be selected - ${title}`,
      requires: (title: string) => `Requires row selection - ${title}`,
    },
    plasmidViewSectionCommands: {
      downloadTitle: "Download",
      downloadCircularAsPNG: "Map View PNG",
      downloadCircularAsPNGTitle: "Download Map View as PNG",
      downloadCircularAsSVG: "Map View SVG",
      downloadCircularAsSVGTitle: "Download Map View as SVG",
      downloadLinearAsPNG: "Sequence View PNG",
      downloadLinearAsPNGTitle: "Download Sequence View as PNG",
      downloadLinearAsPNGTitleFirefox: "Please use another browser to download the Sequence View as PNG",
      downloadLinearAsSVG: "Sequence View SVG",
      downloadLinearAsSVGTitle: "Download Sequence View as SVG",
    },
    downloadFile: {
      error: "An error occurred while downloading the file",
      errorGatewayTimeout: "The file download request took too long (gateway/proxy timeout)",
      unable: "Unable to download the file",
    },
    commandToConfirmCommand: {
      cancel: "Take me back",
      cancelTitle: "Take me back, close the confirmation dialog",
      confirm: "Confirm",
    },
    constraint: {
      mandatory: () => "Value is required",
      javaScriptRegexPattern: (constraint: I.Constraint) =>
        `The value must only contain the following characters: ${constraint.javaScriptRegexPattern as string}`,
      length: (constraint: I.Constraint) => `Length must be <= ${constraint.length as number}`,
      letterCaseUpper: () => "The value must be in upper case",
      letterCaseLower: () => "The value must be in lower case",
      letterCaseCamel: () => "The value must be in camel case",
      precision: (constraint: I.Constraint) => {
        if (!constraint.precision) return "";
        if (constraint.precision.fractionalDigits !== undefined) {
          return `Numeric value out of bounds (<${constraint.precision.integerDigits} digits>.<${constraint.precision.fractionalDigits} digits> expected)`;
        }
        return `Numeric value out of bounds (<${constraint.precision.integerDigits} digits> expected)`;
      },
      rangeMinMax: (constraint: I.Constraint) => {
        if (!constraint.range) return "";
        const { asAbsValue, min, max } = constraint.range;
        const absText = asAbsValue ? "absolute " : "";
        if (min !== undefined && max !== undefined) return `The ${absText}value must be between ${min} and ${max}`;
        if (min === undefined && max !== undefined) return `The ${absText}value must be <= ${max}`;
        if (min !== undefined && max === undefined) return `The ${absText}value must be >= ${min}`;
        return "";
      },
      invalidNumber: () => "Invalid number format",
    },
    hooks: {
      useFileDownload: {
        downloading: "Downloading file...",
        downloadingInfo: "This may take up to several minutes depending on file size",
        downloaded: (filename?: string) => `${filename ? `File '${filename}' downloaded` : "File downloaded"}`,
      },
    },
  },
  App: {
    Header: {
      logout: "Logout",
      more: (count: number) =>
        `${count} more ${pluralize(count, "option", "options")}. Continue typing to refine further.`,
    },
    Notifier: {
      dismiss: "Dismiss",
    },
    ErrorBoundary: {
      title: "Something went wrong.",
    },
  },
  Elements: {
    Denotation: {
      DenotationPartModalHTML: {
        title: "Help",
        buttonTitle: "Click here for more information",
      },
    },
    Value: {
      FormattedValues: {
        expand: (count: number) => `Show ${count} more`,
        collapse: "Show less",
      },
    },
    AutoSuggest: {
      outcomeTimeout: "Sorry, the application seems to be busy right now.",
      outcomeNotAvailable: "Sorry, the autocomplete is not available right now.",
      onlyFirstOptions: (count: number, query?: string) =>
        `Showing only the first ${count} ${pluralize(count, "option", "options")}. ${
          query && query !== "*" ? "Continue typing to refine further." : "Start typing to refine."
        }`,
      moreOptions: (count: number, query?: string) =>
        `${count} more ${pluralize(count, "option", "options")}. ${
          query && query !== "*" ? "Continue typing to refine further." : "Start typing to refine."
        }`,
    },
    ValueEdit: {
      ValueDisabledIcon: {
        title: "Uneditable field",
      },
    },
    Form: {
      InputAdvancedSelect: {
        more: "Continue typing to refine further",
        showMore: (optionsHidden: number, optionsForNextPage: number) =>
          optionsHidden === optionsForNextPage
            ? `Show additional ${optionsForNextPage} hidden ${pluralize(optionsForNextPage, "option", "options")}`
            : `Show additional ${optionsForNextPage} (out of ${optionsHidden}) hidden ${pluralize(
                optionsForNextPage,
                "option",
                "options",
              )}`,
      },
      DatePicker: {
        modalTitle: "Visualize date time in a different time zone",
        invalid: (format: string) => `Invalid format (${format})`,
      },
      CheckboxCollection: {
        noOptions: "No option available",
      },
      InputSelect: {
        noOptions: "No option available",
      },
      InputNumber: {
        invalid: "Invalid number format",
      },
    },
    ExpandableText: {
      expand: "Show more",
      collapse: "Show less",
    },
    ExpandableItems: {
      less: "show less",
      more: "show more",
    },
    ExpandableMessages: {
      less: "show less",
      DEBUG: (count: number) => `${count} DEBUG`,
      DETAIL: (count: number) => `${count} ${count > 1 ? "Details" : "Details"}`,
      INFO: (count: number) => `${count} ${count > 1 ? "Infos" : "Info"}`,
      WARN: (count: number) => `${count} ${count > 1 ? "Warnings" : "Warning"}`,
      ERROR: (count: number) => `${count} ${count > 1 ? "Errors" : "Error"}`,
    },
    ItemsPagination: {
      first: "first",
      previous: "previous",
      next: "next",
      last: "last",
    },
    Modal: {
      SimpleModal: {
        close: "Close",
      },
      ModalActions: {
        close: "Close",
        defaultApply: "Apply",
      },
    },
    Table: {
      Table: {
        filterNoResults: "The current filter doesn't match any records. Try to adjust or remove your filter.",
        copiedToClipboard: "The column's content has been copied to clipboard",
        noCountNoMoreResults: "No more records",
        persistUpdatedTableActiveRowSelectionFailed: "The current selection could not be persisted",
      },
      TableRowContent: {
        clearUnit: "Use default column unit to edit this value",
      },
      TableStaleIndicator: {
        tooltip: (date: string) => `Processing data. Last data update ${date}.`,
        tooltipNew: "New data available. Click here to refresh the table.",
        new: "New data available",
      },
      TableHiddenColumnsInfo: {
        button: (count: number) => `${count} hidden columns`,
        title: "Hidden columns",
        hiddenVisibleColumns: (count: number) =>
          `- ${count} ${pluralize(
            count,
            "column is",
            "columns are",
          )} hidden because you reached the limit of visible columns`,
        emptyColumns: (count: number) =>
          `- ${count} ${pluralize(
            count,
            "column is",
            "columns are",
          )} hidden because they have no rows containing data on the current page`,
      },
      ClearFilteringButton: {
        title: "Currently filtering. Click to clear active filters",
        clearError: "Error while clearing the filter",
      },
      EditableTable: {
        add: "Add a new row",
        cantDelete: "Can't be deleted - At least one row is required",
        delete: "Delete row",
        required: "Required",
      },
      FooterCommands: {
        hide: "Hide empty columns",
        show: "Show empty columns",
        hideInfo: (count: number) =>
          `Empty columns are shown. Click to hide ${count} ${pluralize(count, "empty column", "empty columns")}.`,
        showInfo: (count: number) =>
          `Empty columns are hidden. Click to show ${count} ${pluralize(count, "empty column", "empty columns")}.`,
        clearColumnsSettings: "Clear view settings",
        clearColumnsSettingsTitle: "Clear view settings",
        clearColumnsSettingsOptions: {
          all: "All",
          allTitle: "Clear columns width and units changes",
          units: "Units",
          unitsTitle: "Clear all columns units changes",
          columnWidth: "Column width",
          columnWidthTitle: "Clear all columns width changes",
          columnPin: "Pinned column",
          columnPinTitle: "Clear all pinned columns",
          columnPosition: "Column position",
          columnPositionTitle: "Clear all columns position changes",
        },
      },
      HeaderCommands: {
        filterColumns: "Filter columns",
        filterColumnsWithWarnings: "Filter columns (currently active, with warnings)",
        visibilityColumns: "Show or hide columns",
        editable: {
          enabled: "Edit mode activated, double click on any row to edit the values",
          disabled: "Click to enable edit mode, then double click on any row to edit the values",
        },
      },
      ModalColsFiltering: {
        saveNewEmptyInfo: "A filter without condition can't be created",
        updateEmptyInfo: "A filter without condition can't be updated",
        saveNewSuccess: "Filter has been created and applied",
        saveNewError: "Error while persisting the filter",
        saveSuccess: "Filter has been applied",
        saveSuccessWithWarnings: "Filter has been applied with warnings",
        saveError: "Error while persisting the filter",
        updateSuccess: "Filter has been updated and applied",
        updateError: "Error while updating the filter",
        clearedSuccess: "Filter has been cleared",
        deletedSuccess: "Filter has been deleted",
        deleteError: "Error while deleting the filter",
        chooseFilter: "Choose filter...",
        reset: "Discard",
        resetTitle: "Discard filter change",
        clear: "Clear",
        clearTitle: "Clear selected filter",
        createAndApply: "Create Filter & Apply",
        updateAndApply: "Apply & Save Changes",
        apply: "Apply",
        save: "Save",
        saveAs: "Save as New",
        delete: "Delete",
        cancel: "Cancel",
        enterName: "Enter filter name...",
        expression: "Filter expression",
        showExpression: "Show filter expression",
        hideExpression: "Hide filter expression",
        expressionApply: "Update tree",
        expressionApplySuccess: "The fields tree view has been updated to match the expression",
        expressionReset: "Reset",
        expressionInvalid: "Invalid expression",
        FilterRow: {
          and: "All - of the following are true",
          or: "Any - of the following are true",
          chooseProperty: "Choose property",
          valueLabel: "Value(s)",
          dateLabelValue: "Date",
          booleanLabelValue: "Value",
          inputTextHelp: "Comma or space separated list of IDs",
        },
      },
      ModalColsVisibility: {
        saveNewSuccess: "Visibility set has been created and applied",
        saveSuccess: "Visibility set has been applied",
        updateSuccess: "Visibility set has been updated and applied",
        deletedSuccess: "Visibility set has been deleted",
        chooseSetting: "Choose setting...",
        allFiltered: "All",
        allFilteredTitle: "Select all filtered columns",
        noneFiltered: "None",
        noneFilteredTitle: "Deselect all filtered columns",
        resetFiltered: "Reset",
        resetFilteredTitle: "Discard all column selection changes for the current filter",
        reset: "Discard",
        resetTitle: "Discard all column selection changes",
        createAndApply: "Create Setting & Apply",
        updateAndApply: "Apply & Save Changes",
        apply: "Apply",
        saveAs: "Save as New",
        delete: "Delete",
        cancel: "Cancel",
        enterName: "Enter setting name...",
        shared: "Shared",
        sharedTitle: "The new setting will be shared with all users",
        maxShownColumnsInfo: (visiblePropertiesCount: number, maxShownColumns: number) =>
          `${visiblePropertiesCount} columns selected to be visible. ` +
          `The table will display a maximum of ${maxShownColumns} columns.`,
        filteredOutInfo: (unshownPropertyIdsCount: number, unshownVisiblePropertiesCount?: number) =>
          `${unshownPropertyIdsCount} options below have been filtered out${
            unshownVisiblePropertiesCount !== undefined && unshownVisiblePropertiesCount > 0
              ? ` (${unshownVisiblePropertiesCount.toString()} of them selected)`
              : ""
          }`,
        refineFurther: (isFiltering: boolean) => ` ${isFiltering ? "Continue" : "Start"} typing to refine further.`,
        filterPlaceholder: "Filter by name",
        FilterGroupSelect: {
          placeholder: "All column groups",
        },
        Group: {
          filterInfo: (allCount: number, shownCount: number) =>
            `${allCount} out of ${shownCount} columns part of this group are matching the filter`,
          selectAllDisabled: "The query is not matching any columns within this group",
        },
      },
      TableFullscreenToggler: {
        on: "Enter full screen",
        off: "Exit full screen",
      },
      TableHeadColumn: {
        sortingText: {
          ascending: "Ascending - Click to sort in descending order",
          descending: "Descending - Click to clear sorting",
          default: "Click to sort in ascending order",
        },
        hideCommand: "Hide",
        filterCommand: "Filter",
        filterCommandTitle: (canInlineFilterColumn: boolean, canFilterColumn: boolean) => {
          if (canInlineFilterColumn) {
            return "Filter";
          }
          if (canFilterColumn) {
            return "Inline filtering is not available for this column. Open the Filter dialog to filter this column.";
          }
          return "Filtering is not available for this column";
        },
        toggleFilterCommand: "Hide filtering",
        pinCommand: "Pin",
        togglePinCommand: "Unpin",
        moveLeftCommand: "Move left",
        moveRightCommand: "Move right",
        moveAfterCommand: "Move after...",
        changeUnitCommand: "Change Unit",
        moveCommand: "Move",
        copyToClipboardCommand: "Copy",
        copyToClipboardTitleCommand: "Copy content to clipboard",
      },
      TableSelection: {
        generic: {
          selected: (selectedCount?: number, totalCount?: number) => {
            if (selectedCount === undefined || totalCount === undefined) return "Rows selected";

            return selectedCount === totalCount
              ? "All rows selected"
              : pluralize(selectedCount, `${selectedCount} row selected`, `${selectedCount} rows selected`);
          },
          all: "Select all rows",
          allTitle: "Select all rows matching filter",
          clear: "Unselect all",
          clearTitle: "Unselect all rows matching filter",
        },
        features: {
          selected: (selectedCount?: number, totalCount?: number) => {
            if (selectedCount === undefined || totalCount === undefined) return "Features selected";

            return selectedCount === totalCount
              ? "All features selected"
              : pluralize(selectedCount, `${selectedCount} feature selected`, `${selectedCount} features selected`);
          },
          all: "Select all features",
          allTitle: "Select all features matching filter",
          clear: "Unselect all",
          clearTitle: "Unselect all features matching filter",
        },
      },
      TableSelectionOptions: {
        title: "Toggle row selection",
        selectAll: "Select all on page",
        unselect: "Unselect all on page",
        select: "Select all  rows",
        clear: "Unselect all",
      },
    },
  },
  Page: {
    ToC: {
      queryPlaceholder: "Find section...",
      hide: "Hide empty sections",
      show: "Show empty sections",
      hideInfo: "Empty sections are shown. Click to hide empty sections.",
      showInfo: "Empty sections are hidden. Click to show empty sections.",
      openAll: "Open all",
      openAllTitle: "Open all sections",
      closeAll: "Close all",
      closeAllTitle: "Close all sections",
      noMatch: "No section matching",
      title: "Page sections (keyboard shortcut: T, T)",
    },
    PageActionsModal: {
      title: "Page actions",
      queryPlaceholder: "Find action...",
      selection: (ids: string[]) => ` (Selection: ${ids.join(", ")})`,
      selectionAndMore: (ids: string[], more: number) => ` (Selection: ${ids.join(", ")} and ${more} others)`,
      noSelection: " (No row(s) selected)",
    },
    PageHeader: {
      showAllActionsTitle: "Page actions (keyboard shortcut: A, A)",
      editTitle: (title: string) => `${title} (keyboard shortcut: E, E)`,
      history: "History",
      pinHistory: "Pin history",
    },
    PageLeftSidebar: {
      history: "History",
      historyEmpty: "History is empty",
      unpinHistory: "Unpin history",
    },
  },
  Section: {
    PlasmidView: {
      selected: (count: number) => pluralize(count, "1 feature selected", `${count} features selected`),
      selectFeatures: "Transfer to Features Table",
      selectFeatures2: "Transfer to Plasmid Map",
      selectFeaturesTitle: "Transfer selected features to matching rows within features section table",
      unselect: "Unselect all",
      transferSuccess: "The table features selection has been applied to the plasmid view",
      Search: {
        placeholder: "Search sequence...",
        noResult: "No result",
        results: (count: number) => pluralize(count, "1 match", `${count} matches`),
      },
      PlasmidViewCommands: {
        copyTitle: "Copy translations to clipboard",
        copiedToClipboard: (count: number) =>
          `${pluralize(count, "1 translation has", `${count} translations have`)} been copied to the clipboard`,
        features: "Features",
        viewerToggle: "View",
        viewerToggleOptions: {
          both: "Map and Sequence View",
          bothTitle: "Show both Map and Sequences Views",
          circular: "Map View",
          circularTitle: "Show only Map View",
          linear: "Sequence View",
          linearTitle: "Show only Sequence View",
        },
        translationToggle: "Translate",
        translationToggleOptions: {
          none: "No translation",
          noneTitle: "No translation",
          features: "Translate all features",
          featuresTitle: "Translate all features",
          selectedFeatures: "Translate selected features",
          selectedFeaturesTitle: "Translate selected features",
          selection: "Translate selected residues",
          selectionTitle: "Translate selected residues - No selection over origin",
          selectionAllFrame: "Translate selected residues - 6 frames (with highlighted stop codon)",
          selectionAllFrameTitle: "Translate selected residues showing all 6 frames - No selection over origin",
          selectionAllFrameWithColors: "Translate selected residues - 6 frames",
          selectionAllFrameWithColorsTitle:
            "Translate selected residues showing all 6 frames - No selection over origin",
        },
      },
    },
    SectionHeader: {
      globalFilter: (label?: string) => ` - Global Filter ${label || ""}`,
      copiedToClipboard: "Link copied to clipboard",
      copyToClipboard: "Copy link to clipboard",
    },
    PropertiesListing: {
      hide: "Hide empty properties",
      show: "Show empty properties",
      hideInfo: (count: number) =>
        `Empty properties are shown. Click to hide ${count} ${pluralize(count, "empty property", "empty properties")}.`,
      showInfo: (count: number) =>
        `Empty properties are hidden. Click to show ${count} ${pluralize(count, "empty propery", "empty properties")}.`,
    },
  },
  Screens: {
    Search: {
      end: "No more results.",
      results: (count: number) => (count === 0 ? "No result." : `${count} ${pluralize(count, "result", "results")}`),
      placeholder: "Start typing to search...",
      SearchResultItem: {
        createdOrUpdated: (date: string, userCreated: string | undefined, userModified: string | undefined) => {
          if (userModified) {
            return `Updated ${date} by ${userModified}`;
          }
          if (userCreated) {
            return `Created ${date} by ${userCreated}`;
          }

          return `Updated ${date}`;
        },
      },
    },
    DownloadFile: {
      close: "Close window",
      downloading: "Downloading file...",
      downloadingInfo: "This may take up to several minutes depending on file size",
      downloaded: "File downloaded",
      error: "An error occured while downloading the file",
    },
    ErrorPage: {
      info: "The following error has been reported:",
      return: "< Return",
      incident: (id: string) => `Incident ID: ${id}`,
      ConfigurationErrorPage: {
        title: "Application error",
        content: "The application could not be started.",
      },
      NotFoundPage: {
        title: "404 - Page not found",
        content: "We can't seem to find the page you are looking for.",
      },
      ForbiddenPage: {
        title: "403 - Forbidden",
        content: "Access to this page is forbidden.",
      },
      UnhandledPageErrorPage: {
        title: "Page error",
        content: "The page could not be loaded.",
        contentGatewayTimeout: "The page request took too long (gateway/proxy timeout).",
      },
    },
    LoginPage: {
      welcome: (name: string) => `Welcome to ${name}`,
      cantAccess: "Can't access your account?",
      username: "Username",
      password: "Password",
      submit: "Log in",
      httpsOptional: (appName: string) =>
        `<b>https</b> (providing secure connections) is recommended and will be required for future ${appName} versions.`,
      httpsMandatory: (appName: string, adminEmail: string) =>
        `<b>https</b> is required to ensure secure connections while using ${appName}.<br>Please contact the application administrator: ${adminEmail}<br><br><b>Login will be denied.</b>`,
    },
    EditPage: {
      nothingEdited: "Nothing has been edited",
      error: "Edit page error while persisting",
      requiredFieldsMissingWithinSections: (sections: string[]) =>
        `Required fields are missing within ${sections.join(", ")}.`,
    },
  },
  DataAnalyzer: {
    ChartView: {
      maxHistogramCategoricalVariableBins: (max: number, count: number) =>
        `A maximum of ${max} out of ${count} categories are shown within the histogram`,
    },
    Groups: {
      filter: "Filter by category",
      tooMany: (shownCount: number, totalCount: number) =>
        `Too many categories, showing ${shownCount} out of ${totalCount}`,
    },
    DataAnalyzer: {
      loading: "Loading data...",
      sessionError: "Data Analyzer Session Invalid",
      sessionExpired: "Your session has expired due to inactivity. Some actions may not be available anymore.",
      idsFromText: (validCount: number, invalidCount: number, alreadySelectedCount: number) => {
        let text = "";
        if (validCount > 0) {
          text += `${validCount} ${pluralize(validCount, "item", "items")} added to the selection`;
        } else {
          text += "No items added to the selection";
        }
        if (alreadySelectedCount) {
          text += ` (${alreadySelectedCount} ${pluralize(alreadySelectedCount, "was", "were")} already selected)`;
        }
        if (invalidCount) {
          text += `. ${invalidCount} ${pluralize(invalidCount, "item", "items")} could not be found in the chart.`;
        }
        return text;
      },
      selectAxis: {
        scatterPlot: "Please select X/Y axis...",
        histogram: "Please select variables...",
      },
    },
    AxisSelection: {
      similarityTreeToggleOptions: {
        on: "Show",
        onTitle: "Show similarity tree",
        onWithDistance: "Show with distance",
        onWithDistanceTitle: "Show similarity tree with distance values",
        onWithSimilarity: "Show with similarity",
        onWithSimilarityTitle: "Show similarity tree with similarity values",
        off: "Hide",
        offTitle: "Hide similarity tree",
        disabledTitle: "Similarity tree is not available for this variable",
      },
      scaleOptions: {
        linear: "Linear",
        linearTitle: "Linear Scale",
        logarithmic: "Log",
        logarithmicTitle: "Logarithmic Scale",
      },
      dataTypeOptions: {
        numeric: "Numeric data",
        datetime: "Date time data",
        categorical: "Classify data into categories",
        none: "",
      },
      getDefaultVariableGroup: {
        label: "All",
      },
    },
    ChartActionExportAsPng: {
      downloadTitle: "Download as PNG",
      chartViewFileName: {
        scatterPlot: "ScatterPlot",
        histogram: "Histogram",
      },
      processingError:
        "The data analyzer visualization could not be exported to an image (PNG). Please try exporting after refreshing the page.",
    },
    ChartActions: {
      selected: (count: number) => `${pluralize(count, "1 item", `${count} items`)} selected`,
      clearSelection: "Unselect all",
      resetZoom: "Reset Zoom",
      selectionTypeOptions: {
        new: "New",
        newTitle: "Selecting items will clear the current selection and create a new one",
        add: "Add",
        addTitle: "Add additional items to the selection (CTRL + MOUSE)",
        remove: "Remove",
        removeTitle: "Remove items from the selection (SHIFT + MOUSE)",
      },
      chartTypeOptions: {
        pointsBoxPlotsTitle: "Dots and Box Plots",
        pointsTitle: "Dots",
        barsTitle: "Bars",
        lineTitle: "Timeline",
        linesTitle: "Timelines (one timeline per color)",
      },
      tooltipToggleOptions: {
        onTitle: "Tooltip is shown when hovering dots. Click to hide tooltip.",
        offTitle: "Tooltip is hidden when hovering dots. Click to show tooltip.",
      },
      jitterToggleOptions: {
        onTitle: "Jitter is enabled. Click to disable jittering.",
        offTitle: "Jitter is disabled. Click to enable jittering.",
      },
      scrollableToggleOptions: {
        onTitle: "The chart can be scrolled. Click to disable scrolling.",
        offTitle: "The chart can be panned/zoomed. Click to enable scrolling.",
        disabledTitle: "Scrolling can only be enabled when both axes are categorical",
      },
      panningEnabledToggleOptions: {
        onTitle: "Panning is enabled (SPACE + MOUSE). Click to disable panning.",
        offTitle: "Panning is disabled. Click to enable panning (SPACE + MOUSE).",
      },
      zoomEnabledToggleOptions: {
        onTitle: "Zooming is enabled (ALT + MOUSE). Click to disable zooming.",
        offTitle: "Zooming is disabled. Click to enable zooming (ALT + MOUSE).",
      },
      pointStyleOptions: {
        circle: "Circle",
        rect: "Square",
        rectRounded: "Rounded Square",
        rectRot: "Diamond",
        triangle: "Triangle",
      },
      pointSizeOptions: {
        small: "Small",
        medium: "Medium",
        large: "Large",
      },
      pointSizeOptionsTitle: {
        small: "Use the small (default) base size to draw data point",
        medium: "Use the medium base size to draw data point",
        large: "Use the large base size to draw data point",
      },
      pointStyleActive: (pointStyleText: string, pointSizeText: string) =>
        `Point style: ${pointStyleText} (size: ${pointSizeText})`,
    },
    MenuActions: {
      copiedToClipboard: (count: number) =>
        `The ${pluralize(count, "selected point has", `${count} selected points have`)} been copied to the clipboard`,
    },
    SelectionModal: {
      selected: (count: number) => pluralize(count, "1 selected point", `${count} selected points`),
    },
    ImportModal: {
      title: "Select by ID",
      placeholder: "Paste IDs, comma or newline separated",
      apply: "Apply",
    },
    HeaderMenu: {
      help: "Help",
      documentation: "Documentation",
      about: "About",
    },
    utilities: {
      chartTooltipLabel: {
        group: (count: number) => pluralize(count, "Group:", "Groups:"),
        clusterSize: (count: number) => `Number of points: ${count}`,
        filtertedOut: (count: number) => pluralize(count, "1 point filtered out", `${count} points filtered out`),
        ids: (ids: string[]) => `${pluralize(ids.length, "Item", "Items")}: ${ids.join(", ")}`,
        idsAndMore: (ids: string[], moreCount: number) =>
          `${pluralize(ids.length, "Item", "Items")}: ${ids.join(", ")} and ${moreCount} more`,
      },
    },
  },
  SequenceAnalyser: {
    utilities: {
      similarityTree: {
        useSortBySimilarity: {
          success: "The sequences have been sorted by similarity",
        },
      },
    },
    BrowserZoomLevelWarning: {
      warning: "For an optimal viewing experience, we recommend setting the zoom to 100% in the browser",
    },
    SimilarityTree: {
      showDistanceToggleOptions: {
        onTitle: "Distance is shown between sequences. Click to hide distance.",
        offTitle: "Distance is hidden. Click to show distance between sequences.",
      },
      sort: "Sort by Similarity",
      sortTitle: "Sort all sequences by similarity",
      download: "Download as PNG",
    },
    SequenceAnalyser: {
      sessionExpired: "Your session has expired due to inactivity. Some actions may not be available anymore.",
      sequenceAnalyzer: "Sequence Viewer",
      sequences: (count: number) => pluralize(count, "sequence", `${count} sequences`),
      invalidSession: "Sequence Viewer Session Invalid",
      loadingSession: "Loading sequences...",
      creatingSession: "Creating Session...",
      initializingSession: "Initializing Sequence Viewer...",
      runningAlignment: "Performing alignment - This may timeout if it runs too long.",
      alignmentError: "Sequences alignment error - Maybe due to too many sequences or too long sequences?",
    },
    Features: {
      FeaturesTree: {
        identical: "Identical",
        distinct: "Distinct",
        shade: "Shade",
        identicalTitle: "All features should have an identical color",
        distinctTitle: "All features should have a distinct color",
        shadeTitle: "All features should be a shade of the same color",
      },
    },
    HeaderMenu: {
      help: "Help",
      documentation: "Documentation",
      about: "About",
    },
    SequenceTableVisualisations: {
      consensus: {
        title: "Consensus",
        close: "Close",
        insert: "Insert consensus as a sequence",
        success: "The consensus has been added at the bottom of the sequences",
      },
      conservation: {
        title: "Conservation",
        close: "Close",
        download: "Download conservation as PNG",
      },
      logo: {
        title: "Sequence Logo",
        close: "Close",
        download: "Download sequence logo as PNG",
      },
    },
    SequenceTableRowTitle: {
      oppositeStrand: "Opposite strand",
    },
    OppositeStrandsModal: {
      title: "Switch to opposite strands",
      scope: "Sequences",
      apply: "Switch",
    },
    FileExportModal: {
      title: "Export",
      scope: "Scope",
      fileType: "File type",
      apply: "Export",
      all: "All",
      selection: (count: number, start?: number, end?: number) => {
        if (count === 0) return "Selection";
        return `Selection (${count} ${pluralize(count, "sequence", "sequences")}${
          start !== undefined && end !== undefined ? ` - residues ${start} to ${end}` : ""
        })`;
      },
      content: "File content",
      sequences: "Sequences",
      features: "Features",
      featuresRaw: "Features (unaligned)",
    },
    FeaturesToCSVFileExportModal: {
      title: (fileFormat: "csv" | "tsv") =>
        fileFormat === "csv"
          ? "Export feature sequences to comma-separated file"
          : "Export feature sequences to tab-separated file",
      scope: "Scope",
      apply: "Export",
      all: "All",
      selection: (count: number) => {
        if (count === 0) return "Selection";
        return `Selection (${count} ${pluralize(count, "sequence", "sequences")})`;
      },
      content: "File content",
      raw: "Unaligned sequences",
      aligned: "Aligned sequences",
      features: "Feature types",
      includeFullSequence: "Include full sequence",
      error: "The feature sequences could not be exported",
    },
    InsertFromFileModal: {
      title: "Import from file",
      apply: "Import",
      success: "File successfully imported",
    },
    InsertRemovedSequencesModal: {
      title: "Insert removed sequences",
      apply: "Insert",
    },
    InsertActionModal: {
      manual: "Insert sequence",
      sessionStore: "Insert stored sequence",
      required: "Required",
      invalidResidues: "Invalid residues",
      removeGaps: "Remove gaps",
      name: "Name",
      residues: "Residues",
      featuresRetained: "Features will be retained unless the sequence is edited",
    },
    Colors: {
      schemes: "Schemes",
      hydrophobicity: "Hydrophobicity schemes",
      topOccurrences: "Top occurrence only",
    },
    SettingsModal: {
      title: "Settings",
      viewsLabel: "Views",
      views: {
        consensus: "Consensus",
        conservation: "Conservation",
        logo: "Sequence Logo",
      },
      labelCompositionLabel: "Label composition",
      tooltipToggle: "Show residue tooltip information on hover",
      sequencesNavigatorToggle: "Alignment Overview",
    },
    TableActions: {
      ColorsModal: {
        title: "Colors",
        download: "Download color scheme as image (PNG)",
        fileName: "Colors",
      },
      ExportToPNG: {
        loading: "Preparing file...",
        loadingError: "The sequences could not be exported to an image (PNG). Please try exporting less sequences.",
        errorTooMany: (errorCount?: number) =>
          `A maximum of ${errorCount} sequences can be processed to download as image (PNG)`,
        errorTooLong: (count: number) => `Sequences with over ${count} residues can't be exported`,
      },
      resize: "Resize sidebar",
      copiedSequencesToClipboard: (count: number) =>
        `The ${pluralize(
          count,
          "selected sequence has",
          `${count} selected sequences have`,
        )} been copied to the clipboard`,
      copiedIdsToClipboard: (count: number) =>
        `The ${pluralize(
          count,
          "selected entity ID has",
          `${count} selected entities IDs have`,
        )} been copied to the clipboard`,
      copiedToStore: (count: number) =>
        `The ${pluralize(
          count,
          "selected sequence has",
          `${count} selected sequences have`,
        )} been copied to the session store`,
      sortedByLabel: "The sequences have been sorted by label",
      EditSelectionRangeModal: {
        title: "Edit selection range",
        from: "From",
        to: "To",
        save: "Save",
        disable: "Invalid numbers",
      },
      FileExportModal: {
        noFeatures: "The selection doesn't match any feature",
      },
      TableActionsHeader: {
        selectAll: "Select All",
        selectAllTitle: "Select all sequences",
        invert: "Invert",
        invertTitle: "Invert sequences selection",
        editRange: "Select Range",
        editRangeTitle: "Select range",
        clear: "Clear",
        clearTitle: "Clear selection",
        searchRange: "Search selected sequence",
        viewOptions: {
          default: "Default view",
          abbreviated: "Abbreviated view",
          abbreviatedWithoutColors: "Abbreviated view without residues colors",
          abbreviatedWithoutColorsAndFeaturesColors: "Abbreviated view without residues colors and features colors",
        },
        selectionSummary: (selection: SequenceSelection) => {
          if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
            return `${selection.ids.length} seq. (${selection.positionStart + 1} to ${selection.positionEnd + 1})`;
          }
          return pluralize(selection.ids.length, "1 sequence", `${selection.ids.length} sequences`);
        },
        sequences: (count: number) => pluralize(count, "1 sequence", `${count} sequences`),
      },
      AlignActionModal: {
        title: "Align sequences",
        description: (alignmentToolLabel: string) => `Running ${alignmentToolLabel} using the following options:`,
        identityLabel: "Identity required for delay",
        gapCreationPenaltyLabel: "Gap creation penalty",
        gapExtensionPenaltyLabel: "Gap extension penalty",
        apply: "Align",
        previouslyApplied: "Previously applied settings",
      },
      SimilarityTreeModal: {
        title: "Similarity Tree",
      },
      SelectionActions: {
        duplicate: "Duplicate...",
        duplicateTitle: "Duplicate...",
        showHideFeatures: "Show/Hide...",
        showHideFeaturesTitle: "Show/Hide...",
        settings: "Settings",
        settingsTitle: "Settings",
        sortByLabelAsc: "Sort by Label (ascending)",
        sortByLabelAscTitle: "Sort by Label (ascending)",
        sortByLabelDesc: "Sort by Label (descending)",
        sortByLabelDescTitle: "Sort by Label (descending)",
        oppositeStrands: "Switch to Opposite Strands",
        oppositeStrandsTitle: "Switch to opposite strands",
        moveTop: "Move to Top",
        moveTopTitle: "Move Selection to Top",
        moveUp: "Move Up",
        moveUpTitle: "Move selection up",
        moveDown: "Move Down",
        moveDownTitle: "Move selection down",
        sequence: "Sequence",
        sequenceTitle: "Sequence",
        copyResiduesFasta: "Copy with FASTA Header",
        copyResiduesFastaTitle: "Copy selected residues in FASTA format to the clipboard",
        copyToClipboardIds: "Copy Entity IDs",
        copyToClipboardIdsTitle: "Copy selected entities IDs",
        copyResidues: "Copy",
        copyResiduesTitle: "Copy selected residues to the clipboard",
        copyToStore: "Save to Session Store",
        copyToStoreTitle:
          "Save entire selected sequences data including features" +
          " to the session store and insert them later on this instance or another",
        removeSelection: "Hide",
        removeSelectionTitle: "Hide the selection",
        removeGaps: "Remove Gaps",
        removeGapsTitle: "Remove gaps within the selection",
        order: "Order",
        orderTitle: "Order",
        arrangeBySimilarity: "Sort by Similarity",
        arrangeBySimilarityTitle: "Sort by similarity",
        align: "Run...",
        alignTitle: "Run sequences alignment",
        run: "Alignment",
        runTitle: "Alignment",
        insertManual: "Paste...",
        insertManualTitle: "Paste residues",
        insertSessionStore: "Load from Session Store...",
        insertSessionStoreTitle: "Load sequences from session store",
        insertGaps: "Insert Gaps...",
        insertGapsTitle: "Insert gaps",
        insertRemovedSequences: (count: number) => `Unhide${count > 0 ? ` (${count})` : ""}`,
        insertRemovedSequencesTitle: "Insert previously hidden sequences to their initial location",
        export: "Export",
        exportTitle: "Export",
        exportPDF: "PDF File...",
        exportPDFTitle: "Export as PDF file",
        exportFile: "FASTA/MSF File...",
        exportFileTitle: "Export as FASTA/MSF file",
        exportFeaturesCSV: (fileFormat: "csv" | "tsv") =>
          fileFormat === "csv" ? "Comma-separated File..." : "Tab-separated File...",
        exportFeaturesCSVTitle: (fileFormat: "csv" | "tsv") =>
          fileFormat === "csv"
            ? "Export feature sequences to comma-separated file"
            : "Export feature sequences to tab-separated file",
        resetAlign: "Reset",
        resetAlignTitle: "Reset alignment",
        feature: "Feature",
        featureTitle: "Feature",
        expandFeaturesSelected: "Expand for Selected Sequences",
        expandFeaturesSelectedTitle: "Expand for selected sequences",
        collapseFeaturesSelected: "Overlay for Selected Sequences",
        collapseFeaturesSelectedTitle: "Overlay for selected sequences",
        expandFeaturesAll: "Expand for All Sequences",
        expandFeaturesAllTitle: "Expand for all sequences",
        collapseFeaturesAll: "Overlay for All Sequences",
        collapseFeaturesAllTitle: "Overlay for all sequences",
        insertFromFile: "Import from File...",
        insertFromFileTitle: "Import from file",
        colors: "Colors",
        colorsTitle: "Colors",
        views: "Views",
        viewsTitle: "Views",
        openSequencesNavigator: "Alignment Overview",
        openSequencesNavigatorTitle: "Show Alignment Overview",
        openSimilarityTree: "Similarity Tree",
        openSimilarityTreeTitle: "Show Similarity Tree",
        exportPNG: "Image (PNG)",
        exportPNGTitle: "Download as image (PNG)",
      },
    },
    Search: {
      placeholder: "Search sequence, residues...",
      selectionOnly: "Search selection only",
      invalidResidues: "Only residues letter symbols and gaps are valid search characters",
    },
  },
};

export default I18n;
